import { useEffect, useState, type ChangeEvent } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import SearchIcon from '@mui/icons-material/Search';
import DistributorDataTable from '../../components/DistributorDataTable';
import DistributorTable from '../../components/DistributorTable';
import ApiService from '../../services/api';
import Layout from '../../components/Layout';

import { UserSession } from '../../types';
import { useAppContext } from '../../context/AppContext';
// import { filterValidSessions, logSessionData } from '../../utils/dataValidation';

const DistributorUserDetails = () => {
    const { token, setToken, setUserId, distributorId, getTranslation, distributorSessionsCache, updateDistributorSessionsCache, clearDistributorSessionsCache, customerSessionsCache, updateCustomerSessionsCache, clearCustomerSessionsCache, distributorTableView, setDistributorTableView, distributorUserDetailsPages, setDistributorUserDetailsPage } = useAppContext();

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [timeFilter, setTimeFilter] = useState<string>('all');
    const [filteredData, setFilteredData] = useState<UserSession[]>([]);
    /** totalPages reset on remount; refetched from API — current page is kept in context */
    const [totalPagesByView, setTotalPagesByView] = useState<{
        distributor: number;
        customer: number;
    }>({ distributor: 1, customer: 1 });
    const tableView = distributorTableView;
    const currentViewKey = tableView === 'customer' ? 'customer' : 'distributor';
    const savedPages =
        distributorId && distributorUserDetailsPages[distributorId]
            ? distributorUserDetailsPages[distributorId]
            : { distributor: 1, customer: 1 };
    const distPage = savedPages.distributor;
    const custPage = savedPages.customer;
    const currentPage = tableView === 'customer' ? custPage : distPage;
    const totalPages = totalPagesByView[currentViewKey];

    const getSearchPlaceholder = () => {
        const fullText = getTranslation("Txt886", "Search by name, age, gender, skintype or date");
        return fullText.split(' ')[0] || "Search";
    };

    // Clear cache when distributor changes
    useEffect(() => {
        if (distributorId && distributorSessionsCache.distributorId !== distributorId) {
            clearDistributorSessionsCache();
        }
        if (distributorId && customerSessionsCache.distributorId !== distributorId) {
            clearCustomerSessionsCache();
        }
    }, [distributorId, distributorSessionsCache.distributorId, customerSessionsCache.distributorId, clearDistributorSessionsCache, clearCustomerSessionsCache]);

    useEffect(() => {
        const fetchData = async () => {
            if (!token || !distributorId) {
                return;
            }
            
            try {
                // Update the appropriate cache based on view type
                if (tableView === 'customer') {
                    updateCustomerSessionsCache(null, true, null, distributorId);
                } else {
                    updateDistributorSessionsCache(null, true, null, distributorId);
                }
                
                const api = new ApiService(null, token);
                const switchSide = tableView === 'customer' ? 'CS' : 'DS';
                const response = await api.getAllDistributorSessions(currentPage, switchSide);
                const responseData = response.data as { sessions?: UserSession[]; total_sessions_count?: number; page_size?: number };

                const sessions = responseData.sessions || [];
                const totalCount = responseData.total_sessions_count;
                const pageSize = responseData.page_size || Math.max(sessions.length, 10);
                const computedTotal =
                    typeof totalCount === 'number' && totalCount > 0
                        ? Math.max(1, Math.ceil(totalCount / pageSize))
                        : 1;

                if (distributorId && currentPage > computedTotal) {
                    setDistributorUserDetailsPage(distributorId, currentViewKey, computedTotal);
                    return;
                }

                setTotalPagesByView((prev) => {
                    if (prev[currentViewKey] === computedTotal) return prev;
                    return { ...prev, [currentViewKey]: computedTotal };
                });

                // Store in the appropriate cache based on view type
                if (tableView === 'customer') {
                    updateCustomerSessionsCache(sessions, false, null, distributorId);
                } else {
                    updateDistributorSessionsCache(sessions, false, null, distributorId);
                }
            } catch (err: any) {
                console.error('Distributor user details error:', err);
                
                // Update error in the appropriate cache
                if (tableView === 'customer') {
                    updateCustomerSessionsCache(null, false, err.message || 'Failed to fetch customer sessions', distributorId);
                } else {
                    updateDistributorSessionsCache(null, false, err.message || 'Failed to fetch distributor sessions', distributorId);
                }
                
                if (err.message?.includes('Authentication token is required') || 
                    err.message?.includes('Unauthorized') ||
                    err.statusCode === 401) {
                    setToken(null);
                    setUserId(null);
                }
            }
        };
        
        fetchData();
    }, [distributorId, token, tableView, distPage, custPage, setDistributorUserDetailsPage]);
    const handlePageChange = (_event: ChangeEvent<unknown>, newPage: number) => {
        if (distributorId) {
            setDistributorUserDetailsPage(distributorId, currentViewKey, newPage);
        }
    };


    // Filter data based on search term and time filter
    useEffect(() => {
        const profileData = tableView === 'customer' 
            ? (customerSessionsCache.data || [])
            : (distributorSessionsCache.data || []);
        
        let filtered = profileData;

        // Apply time filter
        if (timeFilter !== 'all') {
            const now = new Date();
            const filterDate = new Date();
            
            if (timeFilter === '6months') {
                filterDate.setMonth(now.getMonth() - 6);
            } else if (timeFilter === '1year') {
                filterDate.setFullYear(now.getFullYear() - 1);
            }
            
            filtered = filtered.filter((item) => {
                const itemDate = new Date(item.createdAt);
                return itemDate >= filterDate;
            });
        }

        // Apply search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            
            filtered = filtered.filter((item) => {
        

                // Search in name
                const fullName = `${item.first_name || ''} ${item.last_name || ''}`.toLowerCase();
                if (fullName.includes(searchLower)) return true;

                // search in guest name 
                if (item.guest_name?.toLowerCase().includes(searchLower)) return true;

                // search in guest email
                if (item.guest_email?.toLowerCase().includes(searchLower)) return true;
                
                // Search in email
                if (item.email?.toLowerCase().includes(searchLower)) return true;
                
                // Search in gender
                if (item.gender?.toLowerCase().includes(searchLower)) return true;
                
                // Search in age range
                if (item.age_range?.toLowerCase().includes(searchLower)) return true;
                
                // Search in skin type
                if (item.skin_type?.toLowerCase().includes(searchLower)) return true;
                
                // Search in user_id
                if (item.user_id?.toLowerCase().includes(searchLower)) return true;
                
                return false;
            });
        }
        
        setFilteredData(filtered);
    }, [searchTerm, timeFilter, distributorSessionsCache.data, customerSessionsCache.data, tableView]);

    return (
        <Layout showBackButton={true}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: "20px", md: "20px" },
                    alignItems: { xs: "center", md: "center" },
                    justifyContent: { xs: "flex-start", md: "flex-start" },
                    width: "100%",
                    height: "100%",
                    overflow: { xs: "hidden", md: "visible" },
                }}>

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: { xs: "10px", md: '20px' },
                    width: "100%",
                    height: "auto",
                    overflow: { xs: "visible", md: "visible" },
                }}>
                        <Typography sx={{
                            color: "var(--color-text-primary)",
                            textAlign: "center",
                            fontFamily: "HerbalifeFont",
                            fontSize: { xs: "20px", md: "30px" },
                            fontStyle: "normal",
                            fontWeight: 500,
                        }}>
                            {getTranslation("Txt492")}
                        </Typography>

                        {/* Search Bar and Filter */}
                        <Box sx={{
                            width: "100%",
                            maxWidth: { xs: "100%", md: "80%" },
                            px: { xs: 1, md: 0 },
                            display: "flex",
                            gap: { xs: 1, sm: 2, md: 3 },
                            flexDirection: { xs: "row", sm: "row" },
                            alignItems: { xs: "stretch", sm: "center" },
                            flexWrap: { xs: "nowrap", sm: "nowrap" }
                        }}>
                            <TextField
                                variant="outlined"
                                placeholder={getSearchPlaceholder()}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                dir="ltr"
                                sx={{
                                    flex: { xs: 2, sm: 3, md: 4 },
                                    minWidth: 0,
                                    "& .MuiInputBase-input": {
                                        padding: { xs: "10px 12px", md: "12px 16px" },
                                        fontSize: { xs: "14px", md: "16px" },
                                        direction: "ltr",
                                        textAlign: "left",
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: "var(--color-text-secondary)", fontSize: { xs: "18px", sm: "20px", md: "24px" } }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: "25px",
                                        backgroundColor: "var(--color-background)",
                                        boxShadow: "0px 4px 6px 0px rgba(0, 0, 0, 0.10)",
                                        direction: "ltr",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "var(--color-border-light)",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "var(--color-border-dark)",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "var(--color-border-dark)",
                                        },
                                    }
                                }}
                            />
                            
                            <FormControl 
                                sx={{ 
                                    flex: { xs: 1, sm: 1, md: 1 },
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "25px",
                                        backgroundColor: "var(--color-background)",
                                        boxShadow: "0px 4px 6px 0px rgba(0, 0, 0, 0.10)",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "var(--color-border-light)",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "var(--color-border-dark)",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "var(--color-border-dark)",
                                        },
                                    }
                                }}
                            >
                                <Select
                                    value={timeFilter}
                                    onChange={(e: SelectChangeEvent) => setTimeFilter(e.target.value)}
                                    displayEmpty
                                    fullWidth
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                "& .MuiMenu-list": {
                                                    padding: "8px 0 !important",
                                                    paddingLeft: "0 !important",
                                                },
                                                "& .MuiMenuItem-root": {
                                                    paddingLeft: "10px !important",
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        "& .MuiSelect-select": {
                                            padding: { xs: "10px 12px", sm: "12px 14px", md: "12px 16px" },
                                            fontSize: { xs: "13px", sm: "14px", md: "16px" },
                                            minHeight: { xs: "40px", sm: "44px", md: "48px" },
                                            display: "flex",
                                            alignItems: "center",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        },
                                        "& .MuiSelect-icon": {
                                            fontSize: { xs: "18px", sm: "20px", md: "24px" },
                                            color: "var(--color-text-secondary)",
                                        }
                                    }}
                                >
                                    <MenuItem value="all" sx={{ fontSize: { xs: "13px", sm: "14px", md: "16px" }, pl: 0 }}>
                                        {getTranslation("Txt323")}
                                    </MenuItem>
                                    <MenuItem value="6months" sx={{ fontSize: { xs: "13px", sm: "14px", md: "16px" }, pl: 0 }}>
                                        {getTranslation("Txt307")}
                                    </MenuItem>
                                    <MenuItem value="1year" sx={{ fontSize: { xs: "13px", sm: "14px", md: "16px" }, pl: 0 }}>
                                        {getTranslation("Txt889")}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Table Toggle */}
                        <Box sx={{
                            width: "100%",
                            maxWidth: { xs: "100%", md: "80%" },
                            display: "flex",
                            justifyContent: "center",
                            // mb: { xs: 2, md: 3 }
                        }}>
                            <Box
                                sx={{
                                    position: "relative",
                                    display: "inline-flex",
                                    backgroundColor: "var(--color-background)",
                                    borderRadius: "25px",
                                    boxShadow: "0px 4px 6px 0px rgba(0, 0, 0, 0.10)",
                                    padding: "4px",
                                    gap: 0,
                                }}
                            >
                                <ToggleButtonGroup
                                    value={tableView}
                                    exclusive
                                    onChange={(_e, newView) => {
                                        if (newView !== null) {
                                            setDistributorTableView(newView);
                                        }
                                    }}
                                    aria-label="table view toggle"
                                    sx={{
                                        backgroundColor: "var(--color-background)",
                                        borderRadius: "25px",
                                        position: "relative",
                                        "& .MuiToggleButtonGroup-grouped": {
                                            border: "none",
                                        },
                                        "& .MuiToggleButton-root": {
                                            border: "none",
                                            borderRadius: "21px",
                                            padding: { xs: "8px 20px", md: "10px 28px" },
                                            fontSize: { xs: "13px", sm: "14px", md: "16px" },
                                            fontWeight: 500,
                                            color: "var(--color-text-primary)",
                                            textTransform: "none",
                                            textAlign: "center",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            position: "relative",
                                            zIndex: 1,
                                            transition: "color 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                            width: "auto",
                                            whiteSpace: "nowrap",
                                            "&.Mui-selected": {
                                                backgroundColor: "var(--color-background)",
                                                color: "var(--color-text-inverse)",
                                                "&::before": {
                                                    content: '""',
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    backgroundColor: "var(--color-primary)",
                                                    borderRadius: "21px",
                                                    zIndex: -1,
                                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                                },
                                            },
                                        }
                                    }}
                                >
                                    <ToggleButton value="distributor" aria-label="distributor table">
                                        {getTranslation("Txt1019")}
                                    </ToggleButton>
                                    <ToggleButton value="customer" aria-label="customer table">
                                        {getTranslation("Txt1020")}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                </Box>
                {/* Conditional Table Rendering */}
                <Box
                    sx={{
                        width: "100%",
                        flex: { xs: 1, md: "none" },
                        minHeight: 0,
                        maxHeight: { xs: "100%", md: "none" },
                        display: "flex",
                        flexDirection: "column",
                        overflow: { xs: "hidden", md: "visible" },
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            width: { xs: "100%", md: "90%" },
                            margin: "0 auto",
                            display: "flex",
                            flexDirection: "column",
                            overflow: { xs: "hidden", md: "visible" },
                        }}
                    >
                        {tableView === 'distributor' ? (
                            <DistributorTable profileData={filteredData} />
                        ) : (
                            <DistributorDataTable profileData={filteredData} />
                        )}
                    </Box>
                </Box>
                {totalPages > 1 && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                            marginBottom: { xs: "15px", md: "0px" },
                        }}
                    >
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            shape="rounded"
                            color="secondary"
                            siblingCount={1}
                            boundaryCount={1}
                            sx={{
                                width: { xs: "100%", md: "auto" },
                                "& .MuiPagination-ul": {
                                    flexWrap: "nowrap",
                                    justifyContent: "center",
                                    gap: { xs: 0, sm: 1 },
                                    backgroundColor: "var(--color-background)",
                                    borderRadius: "32px",
                                    padding: { xs: "4px 8px", sm: "6px 12px" },
                                    boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",
                                    // overflowX: "auto",
                                },
                                "& .MuiPaginationItem-root": {
                                    fontSize: { xs: "13px", sm: "14px" },
                                    fontWeight: 600,
                                    color: "var(--color-text-primary)",
                                    borderRadius: "999px",
                                    border: "1px solid transparent",
                                    transition: "all 0.2s ease",
                                    paddingTop: { xs: "2px", sm: "4px" },
                                    minWidth: { xs: 36, sm: 40 },
                                    height: { xs: 36, sm: 40 },
                                    margin: { xs: "0 2px", sm: "0 4px" },
                                    flex: "0 0 auto",
                                },
                                "& .MuiPaginationItem-root.Mui-selected": {
                                    backgroundColor: "var(--color-primary) !important",
                                    color: "var(--color-text-inverse) !important",
                                    borderColor: "var(--color-border-hover) !important",
                                    boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
                                    "&:hover": {
                                        backgroundColor: "var(--color-primary) !important",
                                        color: "var(--color-text-inverse) !important",
                                    },
                                },
                                "& .MuiPaginationItem-root:hover": {
                                    backgroundColor: "var(--color-primary) !important",
                                    color: "var(--color-text-inverse)",
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Layout>
    );
};

export default DistributorUserDetails;
